import { Link } from "react-router-dom";
import { ArrowRight, Landmark, Building2, TrendingUp, Briefcase, GraduationCap, Scale, LineChart, Clock, CheckCircle2, Play, Quote, Star, Sparkles, Globe2, ArrowUpRight, Smile, Heart, PartyPopper, Sun, Linkedin, Award, Home as HomeIcon, Gem, Coins, Factory } from "lucide-react";
import { useEffect, useState } from "react";
import { SERVICES, WHY_US, STATS, BLOGS, TESTIMONIALS, CLIENT_LOGOS, COMPANY, PROCESS, MARKETS, TEAM } from "../mock";
import ContactForm from "../components/ContactForm";

const iconMap = { Landmark, Building2, TrendingUp, Briefcase, GraduationCap, Scale, LineChart, Clock, Home: HomeIcon, Gem, Coins, Factory };

function Counter({ value, suffix }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let start = 0;
    const duration = 1600;
    const step = Math.max(1, Math.ceil(value / (duration / 30)));
    const t = setInterval(() => {
      start += step;
      if (start >= value) { setN(value); clearInterval(t); }
      else setN(start);
    }, 30);
    return () => clearInterval(t);
  }, [value]);
  return <span>{n.toLocaleString()}{suffix}</span>;
}

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#fff8ea] via-[#eff6ff] to-[#dbeafe] pt-14 pb-28">
        <div className="grid-overlay" />
        <div className="blob blob-a" />
        <div className="blob blob-b" />
        <div className="blob blob-c" />
        <div className="confetti-bg" />
        <div className="gold-shimmer" />
        <div className="sparkle-layer">
          <span className="sparkle sparkle-1" />
          <span className="sparkle sparkle-2" />
          <span className="sparkle sparkle-3" />
          <span className="sparkle sparkle-4" />
          <span className="sparkle sparkle-5" />
          <span className="sparkle sparkle-6" />
          <span className="sparkle sparkle-7" />
          <span className="sparkle sparkle-8" />
          <span className="sparkle sparkle-9" />
          <span className="sparkle sparkle-10" />
        </div>

        <div className="container-x relative z-10 grid md:grid-cols-2 gap-10 items-center py-10">
          <div className="fade-in">
            <div className="happy-pill">
              <PartyPopper size={14} /> Where financing feels prosperous
            </div>
            <h1 className="brand-serif text-5xl md:text-6xl font-extrabold text-[#0a2540] leading-[1.05] tracking-tight mt-5">
              Making finance <br />
              <span className="text-gradient">simpler</span> &amp; <span className="gold-foil-text">prosperous</span>
            </h1>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed max-w-lg">
              Fortunexis LLC is a specialised financial consultancy offering trade finance, project funding and corporate advisory services across the GCC and global markets - delivered with a smile.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">Book a Consultation <ArrowRight size={16} /></Link>
              <Link to="/about" className="btn-outline">Explore Services</Link>
            </div>

            {/* Trust chips */}
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <div className="stat-glass !p-4 text-center bounce-in">
                <div className="text-2xl font-extrabold text-[#0a2540]"><Counter value={10} suffix="+" /></div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">Years</div>
              </div>
              <div className="stat-glass !p-4 text-center bounce-in" style={{animationDelay: "0.1s"}}>
                <div className="text-2xl font-extrabold text-[#0a2540]">8</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">Countries</div>
              </div>
              <div className="stat-glass !p-4 text-center bounce-in" style={{animationDelay: "0.2s"}}>
                <div className="text-2xl font-extrabold text-sunshine">
                  <span className="inline-flex items-center gap-1"><Smile size={22} className="text-[#f59e0b]" />4.9</span>
                </div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">Smile Score</div>
              </div>
            </div>
          </div>

          <div className="relative fade-in">
            <div className="relative rounded-[28px] overflow-hidden shadow-2xl border-8 border-white">
              <img src="https://images.unsplash.com/39/lIZrwvbeRuuzqOoWJUEn_Photoaday_CSD%20%281%20of%201%29-5.jpg" alt="Fortunexis team" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0a2540]/40 via-transparent to-transparent" />
            </div>
            <div className="zigzag absolute -top-6 -left-6" />
            <div className="dot-grid absolute -bottom-6 -right-6" />

            {/* Rating card */}
            <div className="absolute -bottom-6 left-6 stat-glass flex items-center gap-3 wobble">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#d4a017] to-[#b45309] text-white flex items-center justify-center">
                <Star size={18} fill="currentColor" />
              </div>
              <div>
                <div className="text-[#0a2540] font-bold text-lg leading-none">Loved by Clients</div>
                <div className="text-[11px] text-slate-500 mt-1">Rated 4.9 out of 5</div>
              </div>
            </div>
            {/* Global card */}
            <div className="absolute -top-4 -right-4 stat-glass hidden md:flex items-center gap-3 wobble">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#2563eb] to-[#0a2540] text-white flex items-center justify-center">
                <Globe2 size={18} />
              </div>
              <div>
                <div className="text-[#0a2540] font-bold text-sm leading-none">Global Reach</div>
                <div className="text-[11px] text-slate-500 mt-1">UAE • UK • US • SG</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS - single celebratory card */}
      <section className="relative -mt-14 z-20">
        <div className="container-x">
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="glass rounded-2xl p-8 flex items-center gap-5 bounce-in">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white bg-gradient-to-br from-[#d4a017] to-[#92400e]">
                <Sparkles size={26} />
              </div>
              <div>
                <div className="text-4xl font-extrabold text-[#0a2540] leading-none"><Counter value={STATS[0].value} suffix={STATS[0].suffix} /></div>
                <div className="text-slate-500 mt-2 font-medium">{STATS[0].label}</div>
              </div>
            </div>
            <div className="glass rounded-2xl p-8 flex items-center gap-5 bounce-in" style={{animationDelay: "0.1s"}}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white bg-gradient-to-br from-[#ec4899] to-[#be185d]">
                <Heart size={26} className="pulse-heart" fill="currentColor" />
              </div>
              <div>
                <div className="text-4xl font-extrabold text-[#0a2540] leading-none">Trust</div>
                <div className="text-slate-500 mt-2 font-medium">Built through relationships</div>
              </div>
            </div>
            <div className="glass rounded-2xl p-8 flex items-center gap-5 bounce-in" style={{animationDelay: "0.2s"}}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white bg-gradient-to-br from-[#22c55e] to-[#15803d]">
                <Sun size={26} />
              </div>
              <div>
                <div className="text-4xl font-extrabold text-[#0a2540] leading-none">Growth</div>
                <div className="text-slate-500 mt-2 font-medium">A brighter financial future</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section hex-bg mt-8">
        <div className="container-x grid md:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-white">
              <img src="https://images.pexels.com/photos/7792841/pexels-photo-7792841.jpeg" alt="consultation" className="w-full h-[460px] object-cover" />
            </div>
            <button className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white text-[#1e40af] flex items-center justify-center shadow-2xl hover:scale-110 transition-transform" aria-label="Play intro video">
              <Play size={22} fill="currentColor" />
            </button>
            <div className="dot-grid absolute -top-8 -left-6" />
            <div className="absolute -bottom-6 -right-6 stat-glass max-w-[220px]">
              <div className="text-xs uppercase tracking-widest text-slate-500">Since 2015</div>
              <div className="text-[#0a2540] font-bold text-lg leading-tight mt-1">Structuring finance across borders</div>
            </div>
          </div>

          <div>
            <div className="section-eyebrow">About Fortunexis</div>
            <h2 className="section-title mt-2">The Best Financial Consultant in Dubai</h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              Fortunexis LLC is a team of trade finance experts with over a decade of experience across global markets. We provide various trade finance services including Letter of Credit, Standby Letter of Credit, Bank Guarantee, Project Funding and Project Finance.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed">
              We help our clients find perfect solutions for their fund requirements with professional excellence, focus and reliability. Looking for a trusted financial advisor in Dubai? We offer a wide range of trade finance and corporate finance services across UAE, Singapore, UK, US, South Africa, Malaysia and Ghana.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-3 text-sm text-slate-700">
              {["Trade Import Finance", "Project Funding", "Corporate Finance", "Private Equity"].map((k) => (
                <li key={k} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#1e40af]" /> {k}</li>
              ))}
            </ul>
            <Link to="/about" className="btn-primary mt-8">Read More <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section bg-[#eff6ff] relative overflow-hidden">
        <div className="blob blob-a" style={{opacity: 0.12}} />
        <div className="container-x relative z-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="section-eyebrow">Services Offered</div>
            <h2 className="section-title mt-2">Full-spectrum <span className="text-gradient">financial solutions</span></h2>
            <p className="text-slate-600 mt-4">End-to-end solutions across trade, debt and equity finance for growing businesses.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {SERVICES.map((s) => {
              const Icon = iconMap[s.icon];
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="service-card group block">
                  <div className="icon-badge mb-5"><Icon size={28} /></div>
                  <h3 className="text-[#0a2540] font-bold text-lg">{s.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.short}</p>
                  <div className="mt-5 text-[#1e40af] text-sm font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Read More <ArrowRight size={14} /></div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section relative overflow-hidden">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto">
            <div className="section-eyebrow">How We Work</div>
            <h2 className="section-title mt-2">A proven <span className="text-gradient">4-step process</span></h2>
            <p className="text-slate-600 mt-4">From discovery to ongoing support - a transparent workflow that gets deals across the line.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14 relative">
            {PROCESS.map((p, i) => (
              <div key={p.step} className="process-step">
                <div className="process-num">{p.step}</div>
                <h3 className="text-[#0a2540] font-bold text-lg mt-3">{p.title}</h3>
                <p className="text-slate-600 mt-2 text-sm leading-relaxed">{p.text}</p>
                {i < PROCESS.length - 1 && (
                  <ArrowRight className="process-connector hidden lg:block" size={26} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section bg-gradient-to-b from-[#eff6ff] to-white">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto">
            <div className="section-eyebrow">Why Choose Us</div>
            <h2 className="section-title mt-2">Why partner with Fortunexis?</h2>
            <p className="text-slate-600 mt-4">Deep expertise, an unbiased approach and an execution-first mindset.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {WHY_US.map((w) => {
              const Icon = iconMap[w.icon];
              return (
                <div key={w.title} className="text-center p-7 rounded-2xl border border-slate-100 hover:border-[#1e40af] hover:shadow-xl transition-all bg-white group">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#dbeafe] to-[#dbeafe] text-[#1e40af] flex items-center justify-center mb-4 group-hover:from-[#1e40af] group-hover:to-[#0a2540] group-hover:text-white transition-all">
                    <Icon size={26} />
                  </div>
                  <h4 className="text-[#0a2540] font-bold">{w.title}</h4>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{w.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GLOBAL MARKETS */}
      <section className="section relative overflow-hidden bg-[#061a3a] text-white">
        <div className="absolute inset-0 opacity-30" style={{backgroundImage: "radial-gradient(circle at 20% 20%, rgba(47,107,216,0.5), transparent 40%), radial-gradient(circle at 80% 60%, rgba(244,181,45,0.25), transparent 40%)"}} />
        <div className="container-x relative z-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[3px] text-[#d4a017] font-semibold">Global Markets</div>
            <h2 className="text-4xl font-extrabold mt-2">Serving clients across <span className="text-[#d4a017]">8 countries</span></h2>
            <p className="text-slate-300 mt-4">A truly global reach with a local touch in every market we operate in.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12">
            {MARKETS.map((m) => (
              <div key={m.code} className="p-5 rounded-2xl bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 hover:border-[#d4a017] transition-all text-center">
                <div className="country-flag">{m.flag}</div>
                <div className="text-sm font-semibold mt-2">{m.country}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{m.code}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT LOGOS */}
      <section className="py-10 border-y border-slate-100 bg-white overflow-hidden">
        <div className="container-x">
          <div className="text-center text-xs uppercase tracking-[3px] text-slate-500 mb-6">Trusted by leading businesses</div>
          <div className="overflow-hidden">
            <div className="logo-strip">
              {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((c, i) => (
                <div key={i} className="text-[#0a2540]/70 font-bold text-xl whitespace-nowrap">{c}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section bg-[#eff6ff]">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="section-eyebrow">Testimonials</div>
            <h2 className="section-title mt-2">What our clients say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white p-7 rounded-2xl border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all">
                <div className="flex items-center justify-between">
                  <Quote size={28} className="text-[#1e40af]" />
                  <div className="flex gap-1 text-[#d4a017]">
                    {[1,2,3,4,5].map((s) => <Star key={s} size={14} fill="currentColor" />)}
                  </div>
                </div>
                <p className="text-slate-600 mt-4 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1e40af] to-[#0a2540] text-white flex items-center justify-center font-bold">{t.name.charAt(0)}</div>
                  <div>
                    <div className="font-bold text-[#0a2540]">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="section bg-white">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="section-eyebrow">Leadership</div>
            <h2 className="section-title mt-2">Meet the <span className="text-gradient">people</span> behind Fortunexis</h2>
            <p className="text-slate-600 mt-4">Global banking pedigree meets a genuinely human approach to finance.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {TEAM.map((m) => (
              <div key={m.name} className="rounded-2xl overflow-hidden bg-white border border-slate-100 hover:shadow-2xl transition-all group">
                <div className="grid grid-cols-5">
                  <div className="col-span-2 h-full overflow-hidden">
                    <img src={m.image} alt={m.name} className="w-full h-full object-cover min-h-[300px] group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="col-span-3 p-6">
                    <h3 className="text-[#0a2540] font-extrabold text-xl">{m.name}</h3>
                    <div className="text-[#1e40af] text-sm font-semibold mt-1">{m.role}</div>
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fff8ea] border border-amber-200 text-[11px] text-[#92400e] font-semibold">
                      <Briefcase size={12} /> {m.prev}
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-[#1e40af] text-sm font-bold">
                      <GraduationCap size={16} /> Education
                    </div>
                    <ul className="mt-2 space-y-1 text-xs text-slate-600">
                      {m.education.slice(0, 2).map((e) => (
                        <li key={e} className="flex items-start gap-2">
                          <span className="w-1 h-1 rounded-full bg-[#1e40af] mt-1.5 shrink-0" /> {e}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 flex items-center gap-2">
                      <a href={m.linkedin} className="w-8 h-8 rounded-full bg-[#0a66c2] text-white flex items-center justify-center hover:scale-110 transition-transform"><Linkedin size={14} /></a>
                      <Link to="/team" className="text-[#1e40af] text-xs font-bold inline-flex items-center gap-1">View Profile <ArrowRight size={12} /></Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/team" className="btn-primary">Meet the Full Team <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section className="section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <div className="section-eyebrow">Latest Insights</div>
              <h2 className="section-title mt-2">From our blog</h2>
            </div>
            <Link to="/blog" className="btn-outline">View All <ArrowRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {BLOGS.map((b) => (
              <article key={b.id} className="blog-card">
                <div className="blog-thumb">
                  <img src={b.image} alt={b.title} />
                </div>
                <div className="p-6">
                  <div className="text-xs text-slate-500 flex items-center gap-3">
                    <span>{b.date}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-400" />
                    <span>{b.author}</span>
                  </div>
                  <h3 className="text-[#0a2540] font-bold text-lg mt-3 hover:text-[#1e40af] transition-colors">{b.title}</h3>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed line-clamp-2">{b.excerpt}</p>
                  <a href="#" className="mt-4 inline-flex items-center gap-1 text-[#1e40af] text-sm font-semibold">Read More <ArrowRight size={14} /></a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative overflow-hidden">
        <div className="container-x">
          <div className="rounded-3xl p-10 md:p-14 bg-gradient-to-r from-[#0a2540] via-[#0f3562] to-[#1e40af] relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-[#d4a017]/30 blur-3xl" />
            <div className="confetti-bg opacity-30" />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="happy-pill mb-3"><Smile size={14} /> Let's build something joyful</div>
                <h3 className="text-3xl md:text-4xl font-extrabold text-white mt-2">Ready to make finance <span className="text-sunshine">feel great?</span></h3>
                <p className="text-slate-200 mt-3 max-w-xl">Book a no-obligation consultation with our advisors and discover the right financing structure for your business - explained clearly, delivered warmly.</p>
              </div>
              <Link to="/contact" className="btn-primary bg-white text-[#0a2540] hover:bg-[#d4a017]">Talk to an Advisor <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section bg-gradient-to-br from-[#dbeafe] to-white">
        <div className="container-x grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="section-eyebrow">Get In Touch</div>
            <h2 className="section-title mt-2">Quick Contact</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">Fill out the form and one of our finance consultants will reach out within one business day.</p>
            <div className="mt-8 rounded-2xl overflow-hidden shadow-lg border-4 border-white">
              <img src="https://images.pexels.com/photos/6077554/pexels-photo-6077554.jpeg" alt="contact" className="w-full h-[320px] object-cover" />
            </div>
            <div className="mt-6 space-y-2 text-sm text-slate-600">
              <div><b className="text-[#0a2540]">Address:</b> {COMPANY.address}</div>
              <div><b className="text-[#0a2540]">Email:</b> {COMPANY.email}</div>
              {COMPANY.phones.map((p) => (
                <div key={p.number}><b className="text-[#0a2540]">{p.label}:</b> {p.number}</div>
              ))}
            </div>
          </div>
          <ContactForm />
        </div>
      </section>
    </div>
  );
}
